// MaturaLinesParamRelationOpenTask.js
// Zadanie maturalne – otwarte (geometria analityczna, prosta)
// Dwie proste w układzie współrzędnych, zależne od parametru m.
// W aktualnym zakresie matury podstawowej generujemy wyłącznie proste równoległe.
// Obsługa prostopadłości w renderowaniu zostaje dla starszych zapisanych podejść.
//
//  - równoległe: oba współczynniki kierunkowe są sparametryzowane,
//    k: y = (A_k m + B_k)x + C_k,  l: y = (A_l m + B_l)x + C_l
//  - prostopadłe: parametr tylko w jednej prostej (losowo: w k albo w l),
//    druga ma stały współczynnik kierunkowy.

import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

// ========= pomocnicze rzeczy do formatowania odpowiedzi =========

function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatMixedFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;

  if (frac === 0) return `${sign}${intPart}`;

  // pozwalamy na mianownik do 25, żeby łapać np. 6/25 zamiast 0.24
  const maxDen = 25;
  for (let d = 1; d <= maxDen; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;

      if (intPart === 0) return `${sign}\\dfrac{${num}}{${den}}`;
      return `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }

  return `${sign}${abs.toFixed(2)}`;
}

function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function getRandomNonZeroInt(min, max) {
  let x;
  do {
    x = getRandomInt(min, max);
  } while (x === 0);
  return x;
}

// ========= GENERATOR =========
//
// Dane w data:
//  relation: "parallel" | "perpendicular"
//
//  kHasParam, lHasParam : bool
//  kA, kB, kC, kConstSlope
//  lA, lB, lC, lConstSlope
//
//  Jeśli hasParam = true, współczynnik kierunkowy = A*m + B.
//  Jeśli hasParam = false, współczynnik kierunkowy = ConstSlope.

function createOriginalTask() {
  return {
    type: "MaturaLinesParamRelationOpenTask",
    data: {
      relation: "parallel",
      kHasParam: true,
      kA: -1,
      kB: 3,
      kC: 5,
      kConstSlope: null,
      lHasParam: true,
      lA: 1,
      lB: 3,
      lC: -4,
      lConstSlope: null,
    },
    answer: {
      parsed: 0,
      raw: "0",
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 24,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") {
    return createOriginalTask();
  }

  const relation = "parallel";
  const kHasParam = true;
  const lHasParam = true;
  const kA = getRandomNonZeroInt(-5, 5);
  let lA;
  do {
    lA = getRandomNonZeroInt(-5, 5);
  } while (lA === kA); // unikamy równania tożsamościowego

  const kB = getRandomInt(-5, 5);
  const lB = getRandomInt(-5, 5);
  const kC = getRandomInt(-5, 5);
  const lC = getRandomInt(-5, 5);
  const kConstSlope = null;
  const lConstSlope = null;
  const m = (lB - kB) / (kA - lA);

  return {
    type: "MaturaLinesParamRelationOpenTask",
    data: {
      relation,
      kHasParam,
      kA,
      kB,
      kC,
      kConstSlope,
      lHasParam,
      lA,
      lB,
      lC,
      lConstSlope,
    },
    answer: {
      parsed: m,
      raw: `${m}`,
    },
  };
}

// ========= TREŚĆ ZADANIA =========

function slopeToString(hasParam, A, B, constSlope) {
  if (hasParam) {
    const main =
      A === 1 ? "m" : A === -1 ? "-m" : `${A}m`;
    if (B === 0) return main;
    return B > 0 ? `${main} + ${B}` : `${main} - ${Math.abs(B)}`;
  }
  // stały współczynnik
  return `${constSlope}`;
}

function cPartString(C) {
  if (C === 0) return "";
  return C > 0 ? ` + ${C}` : ` - ${Math.abs(C)}`;
}

export function renderTask({ data }) {
  const {
    relation,
    kHasParam,
    kA,
    kB,
    kC,
    kConstSlope,
    lHasParam,
    lA,
    lB,
    lC,
    lConstSlope,
  } = data;

  const kSlope = slopeToString(kHasParam, kA, kB, kConstSlope);
  const lSlope = slopeToString(lHasParam, lA, lB, lConstSlope);

  // ⬇️ Nawiasy tylko przy współczynniku z parametrem m
  const kSlopePart = kHasParam ? `(${kSlope})x` : `${kSlope}x`;
  const lSlopePart = lHasParam ? `(${lSlope})x` : `${lSlope}x`;

  const kEq = `k: \\; y = ${kSlopePart}${cPartString(kC)}`;
  const lEq = `l: \\; y = ${lSlopePart}${cPartString(lC)}`;

  const relationText =
    relation === "parallel"
      ? "są równoległe"
      : "są prostopadłe";

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        W kartezjańskim układzie współrzędnych dane są proste{" "}
        <InlineMath math={"k"} /> i <InlineMath math={"l"} /> określone
        równaniami:
      </p>
      <p>
        <InlineMath math={kEq} />
        <br />
        <InlineMath math={lEq} />
      </p>
      <p>
        Dla jakiej wartości parametru{" "}
        <InlineMath math={"m"} /> proste{" "}
        <InlineMath math={"k"} /> oraz{" "}
        <InlineMath math={"l"} /> {relationText}?
      </p>
    </div>
  );
}

// ========= INPUT =========

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-4">
      <span>
        <InlineMath math={"m ="} />
      </span>
      <FractionInput
        value={userAnswer || ""}
        onChange={onChange}
        disabled={disabled}
        label="m"
      />
    </div>
  );
}

// ========= CHECK =========

export function checkAnswer({ answer }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;

  const parsed = userAnswer?.parsed;
  if (parsed == null || isNaN(parsed)) return false;

  return Math.abs(parsed - answer.parsed) < 0.0001;
}

// ========= POPRAWNA ODPOWIEDŹ =========

export function renderAnswer({ answer }) {
  const mLatex = formatMixedFractionLatex(answer.parsed);

  return (
    <div style={{ marginTop: "1rem" }}>
      <InlineMath math={`m = ${mLatex}`} />
    </div>
  );
}

// ========= WYJAŚNIENIE – krok po kroku =========

export function renderExplanation({ data, answer }) {
  const {
    relation,
    kHasParam,
    kA,
    kB,
    kConstSlope,
    lHasParam,
    lA,
    lB,
    lConstSlope,
  } = data;

  const mLatex = formatMixedFractionLatex(answer.parsed);

  if (relation === "parallel") {
    // oba współczynniki parametryczne
    const Ak = kA;
    const Bk = kB;
    const Al = lA;
    const Bl = lB;

    const a1 = (() => {
      const main =
        Ak === 1 ? "m" : Ak === -1 ? "-m" : `${Ak}m`;
      if (Bk === 0) return main;
      return Bk > 0 ? `${main} + ${Bk}` : `${main} - ${Math.abs(Bk)}`;
    })();

    const a2 = (() => {
      const main =
        Al === 1 ? "m" : Al === -1 ? "-m" : `${Al}m`;
      if (Bl === 0) return main;
      return Bl > 0 ? `${main} + ${Bl}` : `${main} - ${Math.abs(Bl)}`;
    })();

    const coeffM = Ak - Al;
    const rhsNum = Bl - Bk;

    return (
      <div className="space-y-4" style={{ textAlign: "left" }}>
        <div>
          <strong>Krok 1.</strong> Proste są równoległe, jeśli ich
          współczynniki kierunkowe są równe:
          <BlockMath math={"a_1 = a_2"} />
          <BlockMath math={`${a1} = ${a2}`} />
        </div>

        <div>
          <strong>Krok 2.</strong> Przenosimy wyrazy z{" "}
          <InlineMath math={"m"} /> na jedną stronę równania i
          rozwiązujemy je:
          <BlockMath math={`${coeffM}m = ${rhsNum}`} />
          <BlockMath math={`m = \\dfrac{${rhsNum}}{${coeffM}} = ${mLatex}`} />
        </div>
      </div>
    );
  }

  // relation === "perpendicular"
  // parametr tylko w jednej prostej
  const paramInK = kHasParam && !lHasParam;
  const paramA = paramInK ? kA : lA;
  const paramB = paramInK ? kB : lB;
  const constSlope = paramInK ? lConstSlope : kConstSlope;

  const constSlopeLatex =
    constSlope < 0 ? `(${constSlope})` : `${constSlope}`;

  const paramSlopeLatex = (() => {
    const main =
      paramA === 1 ? "m" : paramA === -1 ? "-m" : `${paramA}m`;
    if (paramB === 0) return main;
    return paramB > 0
      ? `${main} + ${paramB}`
      : `${main} - ${Math.abs(paramB)}`;
  })();

  const rhs1 = `-\\dfrac{1}{${constSlopeLatex}}`;
  const rhsSimplified =
    constSlope < 0
      ? `\\dfrac{1}{${Math.abs(constSlope)}}`
      : rhs1;

  const rhsForSub = rhsSimplified;
  const rightMinusB = `${rhsForSub} - (${paramB})`;

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong> Proste są prostopadłe, jeśli iloczyn ich
        współczynników kierunkowych jest równy{" "}
        <InlineMath math={"-1"} />:
        <BlockMath math={"a_1 \\cdot a_2 = -1"} />
        <BlockMath
          math={`${paramSlopeLatex} \\cdot ${constSlopeLatex} = -1`}
        />
      </div>

      <div>
        <strong>Krok 2.</strong> Dzielimy obie strony równania przez{" "}
        <InlineMath math={constSlopeLatex} />:
        <BlockMath
          math={`${paramSlopeLatex} = ${rhs1}${
            constSlope < 0 ? ` = ${rhsSimplified}` : ""
          }`}
        />

        <p>Następnie przenosimy wyraz wolny na drugą stronę:</p>
        <BlockMath math={`${paramA}m = ${rightMinusB}`} />

        <p>Dzielenie przez współczynnik przy parametrze daje:</p>
        <BlockMath
          math={`m = \\dfrac{${rightMinusB}}{${paramA}} = ${mLatex}`}
        />
      </div>
    </div>
  );
}

// ========= ODPOWIEDŹ UŻYTKOWNIKA =========

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  const latex = formatLatexPreview(userAnswer.raw, "m");

  return (
    <div style={{ marginTop: "1rem" }}>
      <InlineMath math={latex} />
    </div>
  );
}
